import { UserProfile } from "../repository";
import http from "../http";
import {
  Course,
  EnrolledTraining,
  Interest,
  School,
  User,
  UserAccess,
  UserGeneralStatistics,
  UserRanking,
  UserTeam,
} from "../../../domain/entities/entities";
import { wrapErrorHandler } from "../utils/wrap_error_handler";
import { EventCourse } from "../../../domain/entities/courseEvent/courseEvent";
import { TrainingCourseRequest } from "../../../domain/entities/courseEvent/training_course";
import { Order } from "../../../domain/entities/order";
import { ListResponse } from "domain/entities/shop/product/listResponse";

class _UserProfile implements UserProfile {
  async get(): Promise<User> {
    return wrapErrorHandler<User>(async () => {
      const response = await http.get("/api/user/me/list/personalInformation");

      const user: User = {
        ...response.data.user,
        companies: response.data.companies ?? []
      };

      localStorage.setItem("user", JSON.stringify(user));
      return user;
    });
  }

  async listUserCompanies(): Promise<[]> {
    return wrapErrorHandler<[]>(async () => {
      const response = await http.get("/api/decola/user/companies");
      return response.data.companies;
    });
  }

  async listUserInterests(): Promise<Interest[]> {
    return wrapErrorHandler<Interest[]>(async () => {
      const response = await http.get("/api/decola/user/interests");
      return response.data.interests;
    });
  }

  async editUserInterests(interests: Array<Interest>): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      await http.post("/api/decola/interest/link", { interests: interests });
    });
  }

  async edit(user: User): Promise<void> {
    return wrapErrorHandler<void>(async () => {
      await http.post("/api/decola/user/me/edit/personalInformation", {
        user: user,
      });
    });
  }

  async getUserCoursesInProgress(): Promise<Course[]> {
    return wrapErrorHandler<Course[]>(async () => {
      const response = await http.get("api/decola/course/list/progress");
      return response.data;
    });
  }

  async getUserEnrolledTraining(order: Order): Promise<EnrolledTraining[]> {
    return wrapErrorHandler<EnrolledTraining[]>(async () => {
      const response = await http.get("api/decola/courses/enrolled", {
        params: {
          search: order.search
        }
      });
      return response.data;
    });
  }

  async getUserEvents(): Promise<EventCourse[]> {
    return wrapErrorHandler<EventCourse[]>(async () => {
      const response = await http.get("/api/decola/event/list/attended");
      return response.data;
    });
  }

  async getUserEvent(eventId: number): Promise<EventCourse> {
    return wrapErrorHandler<EventCourse>(async () => {
      return (await http.get(`/api/decola/event/${eventId}`)).data.event;
    });
  }

  async getUserTraining(eventId: number): Promise<TrainingCourseRequest> {
    return wrapErrorHandler<TrainingCourseRequest>(async () => {
      return (await http.get(`/api/decola/graduation/${eventId}`)).data;
    });
  }

  async validateUserIsLogged(): Promise<void> {
    await http.get("/api/user/me/list/personalInformation");
  }

  async getTeams(): Promise<UserTeam[]> {
    const { data } = await http.get("/api/decola/user/teams");
    return data;
  }

  async getUsersRanking(): Promise<UserRanking[]> {
    const { data } = await http.get("/api/decola/list/students/engagement");
    return data;
  }

  async getGeneralStatistics(): Promise<UserGeneralStatistics> {
    const { data } = await http.get("/api/decola/get/statistics/user");
    return data;
  }

  async getAccessesStatistics(): Promise<UserAccess[]> {
    const { data } = await http.get(
      "/api/decola/statistics/user/access?period=day&interval=14"
    );
    return data;
  }

  getUserCertificate(trainingID: number): Promise<string> {
    return wrapErrorHandler<string>(async () => {
      const response = await http.get(
        `/api/decola/certificate/graduation/${trainingID}`
      );
      return response.data.path;
    });
  }

  async listUsers(order: Order): Promise<ListResponse<User>> {
    return wrapErrorHandler<ListResponse<User>>(async () => {
      const response = await http.get(`/api/decola/list/users`, {
        params: {
          page: order.page,
          search: order.search,
          limit: 30,
          orderBy: order.orderBy,
          ordinationAsc: order.isAsc,
        },
      });
      return response.data;
    });
  }

  indicationTrainingUser(userIDs: number[], courseID: number): Promise<number> {
    return wrapErrorHandler<number>(async () => {
      const response = await http.post(
        `/api/decola/indication/training/${courseID}`,
        {
          userIDs,
        }
      );
      return response.data;
    });
  }
}

export const userProfileRepository: UserProfile = new _UserProfile();
